/**
 * Canonical URL map for multi-destination architecture.
 * Paths mirror src/pages/destinations/{country}/ folder structure.
 */
export type LocalizedPath = { pt: string; en: string };

export type DestinationRouteMap = {
  hub: LocalizedPath;
  visas: {
    all: LocalizedPath;
    golden: LocalizedPath;
    startup: LocalizedPath;
    d1: LocalizedPath;
    d2: LocalizedPath;
    d3: LocalizedPath;
    d4: LocalizedPath;
    d5: LocalizedPath;
    d6: LocalizedPath;
    d7: LocalizedPath;
    d8: LocalizedPath;
    eligibility?: LocalizedPath;
  };
  realEstate: LocalizedPath;
  buyProperty: LocalizedPath;
  financing: LocalizedPath;
  business: LocalizedPath;
  legal: LocalizedPath;
  healthcare: LocalizedPath;
  guides: LocalizedPath;
  incentives: LocalizedPath;
  retirementGuide: LocalizedPath;
  investmentGuide: LocalizedPath;
  nomadGuide: LocalizedPath;
  businessGuide: LocalizedPath;
};

const pt = (path: string) => ({ pt: path, en: path });

export const DESTINATION_ROUTES = {
  portugal: {
    hub: pt("/destinations/portugal"),
    visas: {
      all: { pt: "/destinations/portugal/vistos", en: "/destinations/portugal/visas" },
      golden: { pt: "/destinations/portugal/visto-gold", en: "/destinations/portugal/golden-visa" },
      startup: { pt: "/destinations/portugal/startup-visa", en: "/destinations/portugal/startup-visa-en" },
      d1: { pt: "/destinations/portugal/visto-d1", en: "/destinations/portugal/visa-d1" },
      d2: { pt: "/destinations/portugal/visto-d2", en: "/destinations/portugal/visa-d2" },
      d3: { pt: "/destinations/portugal/visto-d3", en: "/destinations/portugal/visa-d3" },
      d4: { pt: "/destinations/portugal/visto-d4", en: "/destinations/portugal/visa-d4" },
      d5: { pt: "/destinations/portugal/visto-d5", en: "/destinations/portugal/visa-d5" },
      d6: { pt: "/destinations/portugal/visto-d6", en: "/destinations/portugal/visa-d6" },
      d7: { pt: "/destinations/portugal/visto-d7", en: "/destinations/portugal/visa-d7" },
      d8: { pt: "/destinations/portugal/visto-d8", en: "/destinations/portugal/visa-d8" },
      eligibility: { pt: "/destinations/portugal/golden-visa-eligibility", en: "/destinations/portugal/golden-visa-eligibility" },
    },
    realEstate: { pt: "/destinations/portugal/imobiliario", en: "/destinations/portugal/real-estate" },
    buyProperty: { pt: "/destinations/portugal/comprar-casa", en: "/destinations/portugal/buy-property" },
    financing: { pt: "/destinations/portugal/financiamento", en: "/destinations/portugal/financing" },
    business: { pt: "/destinations/portugal/negocios", en: "/destinations/portugal/business" },
    legal: { pt: "/destinations/portugal/consultoria-legal", en: "/destinations/portugal/legal-consulting" },
    healthcare: { pt: "/destinations/portugal/saude", en: "/destinations/portugal/healthcare" },
    guides: { pt: "/destinations/portugal/guias-emigrantes", en: "/destinations/portugal/expat-guides" },
    incentives: { pt: "/destinations/portugal/incentivos", en: "/destinations/portugal/incentives" },
    retirementGuide: { pt: "/destinations/portugal/guia-reforma/:region", en: "/destinations/portugal/retirement-guide/:region" },
    investmentGuide: { pt: "/destinations/portugal/guia-investimento/:region", en: "/destinations/portugal/investment-guide/:region" },
    nomadGuide: { pt: "/destinations/portugal/guia-nomada/:region", en: "/destinations/portugal/nomad-guide/:region" },
    businessGuide: { pt: "/destinations/portugal/guia-negocios/:region", en: "/destinations/portugal/business-guide/:region" },
  },
  spain: {
    hub: pt("/destinations/spain"),
    visas: {
      all: { pt: "/destinations/spain/vistos", en: "/destinations/spain/visas" },
      golden: { pt: "/destinations/spain/golden-visa", en: "/destinations/spain/golden-visa" },
      startup: { pt: "/destinations/spain/startup-visa", en: "/destinations/spain/startup-visa" },
      d1: { pt: "/destinations/spain/visto-d1", en: "/destinations/spain/visa-d1" },
      d2: { pt: "/destinations/spain/visto-d2", en: "/destinations/spain/visa-d2" },
      d3: { pt: "/destinations/spain/visto-d3", en: "/destinations/spain/visa-d3" },
      d4: { pt: "/destinations/spain/visto-d4", en: "/destinations/spain/visa-d4" },
      d5: { pt: "/destinations/spain/visto-d5", en: "/destinations/spain/visa-d5" },
      d6: { pt: "/destinations/spain/visto-d6", en: "/destinations/spain/visa-d6" },
      d7: { pt: "/destinations/spain/visto-d7", en: "/destinations/spain/visa-d7" },
      d8: { pt: "/destinations/spain/visto-d8", en: "/destinations/spain/visa-d8" },
    },
    realEstate: { pt: "/destinations/spain/imobiliario", en: "/destinations/spain/real-estate" },
    buyProperty: { pt: "/destinations/spain/comprar-casa", en: "/destinations/spain/buy-property" },
    financing: { pt: "/destinations/spain/financiamento", en: "/destinations/spain/financing" },
    business: { pt: "/destinations/spain/negocios", en: "/destinations/spain/business" },
    legal: { pt: "/destinations/spain/consultoria-legal", en: "/destinations/spain/legal-consulting" },
    healthcare: { pt: "/destinations/spain/saude", en: "/destinations/spain/healthcare" },
    guides: { pt: "/destinations/spain/guias-emigrantes", en: "/destinations/spain/expat-guides" },
    incentives: { pt: "/destinations/spain/incentivos", en: "/destinations/spain/incentives" },
    retirementGuide: { pt: "/destinations/spain/guia-reforma/:region", en: "/destinations/spain/retirement-guide/:region" },
    investmentGuide: { pt: "/destinations/spain/guia-investimento/:region", en: "/destinations/spain/investment-guide/:region" },
    nomadGuide: { pt: "/destinations/spain/guia-nomada/:region", en: "/destinations/spain/nomad-guide/:region" },
    businessGuide: { pt: "/destinations/spain/guia-negocios/:region", en: "/destinations/spain/business-guide/:region" },
  },
  italy: {
    hub: pt("/destinations/italy"),
    visas: {
      all: { pt: "/destinations/italy/vistos", en: "/destinations/italy/visas" },
      golden: { pt: "/destinations/italy/golden-visa", en: "/destinations/italy/golden-visa" },
      startup: { pt: "/destinations/italy/startup-visa", en: "/destinations/italy/startup-visa" },
      d1: { pt: "/destinations/italy/visto-d1", en: "/destinations/italy/visa-d1" },
      d2: { pt: "/destinations/italy/visto-d2", en: "/destinations/italy/visa-d2" },
      d3: { pt: "/destinations/italy/visto-d3", en: "/destinations/italy/visa-d3" },
      d4: { pt: "/destinations/italy/visto-d4", en: "/destinations/italy/visa-d4" },
      d5: { pt: "/destinations/italy/visto-d5", en: "/destinations/italy/visa-d5" },
      d6: { pt: "/destinations/italy/visto-d6", en: "/destinations/italy/visa-d6" },
      d7: { pt: "/destinations/italy/visto-d7", en: "/destinations/italy/visa-d7" },
      d8: { pt: "/destinations/italy/visto-d8", en: "/destinations/italy/visa-d8" },
    },
    realEstate: { pt: "/destinations/italy/imobiliario", en: "/destinations/italy/real-estate" },
    buyProperty: { pt: "/destinations/italy/comprar-casa", en: "/destinations/italy/buy-property" },
    financing: { pt: "/destinations/italy/financiamento", en: "/destinations/italy/financing" },
    business: { pt: "/destinations/italy/negocios", en: "/destinations/italy/business" },
    legal: { pt: "/destinations/italy/consultoria-legal", en: "/destinations/italy/legal-consulting" },
    healthcare: { pt: "/destinations/italy/saude", en: "/destinations/italy/healthcare" },
    guides: { pt: "/destinations/italy/guias-emigrantes", en: "/destinations/italy/expat-guides" },
    incentives: { pt: "/destinations/italy/incentivos", en: "/destinations/italy/incentives" },
    retirementGuide: { pt: "/destinations/italy/guia-reforma/:region", en: "/destinations/italy/retirement-guide/:region" },
    investmentGuide: { pt: "/destinations/italy/guia-investimento/:region", en: "/destinations/italy/investment-guide/:region" },
    nomadGuide: { pt: "/destinations/italy/guia-nomada/:region", en: "/destinations/italy/nomad-guide/:region" },
    businessGuide: { pt: "/destinations/italy/guia-negocios/:region", en: "/destinations/italy/business-guide/:region" },
  },
  greece: {
    hub: pt("/destinations/greece"),
    visas: {
      all: { pt: "/destinations/greece/vistos", en: "/destinations/greece/visas" },
      golden: { pt: "/destinations/greece/golden-visa", en: "/destinations/greece/golden-visa" },
      startup: { pt: "/destinations/greece/startup-visa", en: "/destinations/greece/startup-visa" },
      d1: { pt: "/destinations/greece/visto-d1", en: "/destinations/greece/visa-d1" },
      d2: { pt: "/destinations/greece/visto-d2", en: "/destinations/greece/visa-d2" },
      d3: { pt: "/destinations/greece/visto-d3", en: "/destinations/greece/visa-d3" },
      d4: { pt: "/destinations/greece/visto-d4", en: "/destinations/greece/visa-d4" },
      d5: { pt: "/destinations/greece/visto-d5", en: "/destinations/greece/visa-d5" },
      d6: { pt: "/destinations/greece/visto-d6", en: "/destinations/greece/visa-d6" },
      d7: { pt: "/destinations/greece/visto-d7", en: "/destinations/greece/visa-d7" },
      d8: { pt: "/destinations/greece/visto-d8", en: "/destinations/greece/visa-d8" },
    },
    realEstate: { pt: "/destinations/greece/imobiliario", en: "/destinations/greece/real-estate" },
    buyProperty: { pt: "/destinations/greece/comprar-casa", en: "/destinations/greece/buy-property" },
    financing: { pt: "/destinations/greece/financiamento", en: "/destinations/greece/financing" },
    business: { pt: "/destinations/greece/negocios", en: "/destinations/greece/business" },
    legal: { pt: "/destinations/greece/consultoria-legal", en: "/destinations/greece/legal-consulting" },
    healthcare: { pt: "/destinations/greece/saude", en: "/destinations/greece/healthcare" },
    guides: { pt: "/destinations/greece/guias-emigrantes", en: "/destinations/greece/expat-guides" },
    incentives: { pt: "/destinations/greece/incentivos", en: "/destinations/greece/incentives" },
    retirementGuide: { pt: "/destinations/greece/guia-reforma/:region", en: "/destinations/greece/retirement-guide/:region" },
    investmentGuide: { pt: "/destinations/greece/guia-investimento/:region", en: "/destinations/greece/investment-guide/:region" },
    nomadGuide: { pt: "/destinations/greece/guia-nomada/:region", en: "/destinations/greece/nomad-guide/:region" },
    businessGuide: { pt: "/destinations/greece/guia-negocios/:region", en: "/destinations/greece/business-guide/:region" },
  },
} as const satisfies Record<string, DestinationRouteMap>;

/** Global routes (not under /destinations/) */
export const GLOBAL_ROUTES = {
  home: "/",
  about: { pt: "/sobre-nos", en: "/about-us" },
  contact: { pt: "/contato", en: "/contact" },
  partners: "/parceiros",
  blog: "/blog",
  pathways: "/pathways",
  interactive: "/interactive",
  privacy: { pt: "/politica-privacidade", en: "/privacy-policy" },
  terms: { pt: "/termos-uso", en: "/terms-of-use" },
  gdpr: { pt: "/rgpd", en: "/gdpr" },
} as const;

/** Canonical PT↔EN pairs for Portugal healthcare subpages (hub lives on DESTINATION_ROUTES). */
export const PORTUGAL_HEALTHCARE_SUBPAGES = {
  publicVsPrivate: {
    pt: "/destinations/portugal/saude/publico-vs-privado",
    en: "/destinations/portugal/healthcare/public-vs-private",
  },
  healthInsurance: {
    pt: "/destinations/portugal/saude/seguros-saude",
    en: "/destinations/portugal/healthcare/health-insurance",
  },
  privateHospitals: {
    pt: "/destinations/portugal/saude/hospitais-privados",
    en: "/destinations/portugal/healthcare/private-hospitals",
  },
  visaHealthcare: {
    pt: "/destinations/portugal/saude/vistos-saude",
    en: "/destinations/portugal/healthcare/visa-healthcare",
  },
  dentalCare: {
    pt: "/destinations/portugal/saude/cuidados-dentarios",
    en: "/destinations/portugal/healthcare/dental-care",
  },
  elderlyCare: {
    pt: "/destinations/portugal/saude/cuidados-idosos",
    en: "/destinations/portugal/healthcare/elderly-care",
  },
  emergencyServices: {
    pt: "/destinations/portugal/saude/servicos-emergencia",
    en: "/destinations/portugal/healthcare/emergency-services",
  },
  snsRegistration: {
    pt: "/destinations/portugal/saude/inscricao-sns",
    en: "/destinations/portugal/healthcare/sns-registration",
  },
} as const satisfies Record<string, LocalizedPath>;

function healthcareSubpageLanguageMap(): Record<string, LocalizedPath> {
  const map: Record<string, LocalizedPath> = {};
  for (const route of Object.values(PORTUGAL_HEALTHCARE_SUBPAGES)) {
    map[route.pt] = route;
    map[route.en] = route;
  }
  return map;
}

export function getLocalizedPath(
  route: LocalizedPath,
  lang: "pt" | "en"
): string {
  return route[lang];
}

export function getPortugalPath(
  key: keyof Omit<DestinationRouteMap, "visas"> | `visas.${keyof DestinationRouteMap["visas"]}`,
  lang: "pt" | "en"
): string {
  const routes = DESTINATION_ROUTES.portugal;
  if (key.startsWith("visas.")) {
    const visaKey = key.replace("visas.", "") as keyof DestinationRouteMap["visas"];
    return routes.visas[visaKey][lang];
  }
  return (routes[key as keyof Omit<DestinationRouteMap, "visas">] as LocalizedPath)[lang];
}

export function collectLocalizedPaths(value: unknown, paths: LocalizedPath[] = []): LocalizedPath[] {
  if (!value || typeof value !== "object") return paths;
  if ("pt" in value && "en" in value && typeof (value as LocalizedPath).pt === "string") {
    paths.push(value as LocalizedPath);
    return paths;
  }
  for (const nested of Object.values(value as Record<string, unknown>)) {
    collectLocalizedPaths(nested, paths);
  }
  return paths;
}

/** All Portugal canonical paths grouped by language (excludes :region placeholders). */
export function getPortugalCanonicalPaths(lang: "pt" | "en"): string[] {
  return collectLocalizedPaths(DESTINATION_ROUTES.portugal)
    .map((route) => route[lang])
    .filter((path) => !path.includes(":"));
}

const PORTUGAL_EN_CANONICAL_PATHS = new Set(getPortugalCanonicalPaths("en"));

/** True when path is a PT-slug page under /destinations/portugal (for cross-domain canonical). */
export function isPortugalPtCanonicalPath(path: string): boolean {
  if (!path.startsWith("/destinations/portugal")) return false;
  if (path === "/destinations/portugal") return true;
  for (const enPath of PORTUGAL_EN_CANONICAL_PATHS) {
    // The hub is registered with the same path in both languages. Treating that
    // path as a prefix would mark every child — including visto-gold — as English.
    if (enPath === "/destinations/portugal") continue;
    if (path === enPath || path.startsWith(`${enPath}/`)) return false;
  }
  return true;
}

/** Language switcher map for Portugal canonical routes (+ legacy keys still in use). */
export function buildPortugalLanguageRouteMap(): Record<string, LocalizedPath> {
  const map: Record<string, LocalizedPath> = {};
  for (const route of collectLocalizedPaths(DESTINATION_ROUTES.portugal)) {
    map[route.pt] = route;
    map[route.en] = route;
  }
  return { ...map, ...healthcareSubpageLanguageMap() };
}

/** All destination canonical routes (PT, ES, IT, GR) keyed by either localized slug. */
export function buildAllDestinationsLanguageRouteMap(): Record<string, LocalizedPath> {
  const map: Record<string, LocalizedPath> = {};
  for (const destination of Object.values(DESTINATION_ROUTES)) {
    for (const route of collectLocalizedPaths(destination)) {
      map[route.pt] = route;
      map[route.en] = route;
    }
  }
  return map;
}

/** Legacy + global slugs still used in page `url` props and old App routes. */
const LEGACY_SITE_LANGUAGE_ROUTES: Record<string, LocalizedPath> = {
  "/": { pt: "/", en: "/" },
  "/negocios": { pt: "/negocios", en: "/business" },
  "/business": { pt: "/negocios", en: "/business" },
  "/todos-os-vistos": { pt: "/todos-os-vistos", en: "/all-visas" },
  "/all-visas": { pt: "/todos-os-vistos", en: "/all-visas" },
  "/visto-gold": { pt: "/visto-gold", en: "/golden-visa" },
  "/golden-visa": { pt: "/visto-gold", en: "/golden-visa" },
  "/startup-visa-portugal": { pt: "/startup-visa-portugal", en: "/startup-visa-portugal-en" },
  "/startup-visa-portugal-en": { pt: "/startup-visa-portugal", en: "/startup-visa-portugal-en" },
  "/visto-d1": { pt: "/visto-d1", en: "/visa-d1" },
  "/visa-d1": { pt: "/visto-d1", en: "/visa-d1" },
  "/visto-d2": { pt: "/visto-d2", en: "/visa-d2" },
  "/visa-d2": { pt: "/visto-d2", en: "/visa-d2" },
  "/visto-d3": { pt: "/visto-d3", en: "/visa-d3" },
  "/visa-d3": { pt: "/visto-d3", en: "/visa-d3" },
  "/visto-d4": { pt: "/visto-d4", en: "/visa-d4" },
  "/visa-d4": { pt: "/visto-d4", en: "/visa-d4" },
  "/visto-d5": { pt: "/visto-d5", en: "/visa-d5" },
  "/visa-d5": { pt: "/visto-d5", en: "/visa-d5" },
  "/visto-d6": { pt: "/visto-d6", en: "/visa-d6" },
  "/visa-d6": { pt: "/visto-d6", en: "/visa-d6" },
  "/visto-d7": { pt: "/visto-d7", en: "/visa-d7" },
  "/visa-d7": { pt: "/visto-d7", en: "/visa-d7" },
  "/visto-d8": { pt: "/visto-d8", en: "/visa-d8" },
  "/visa-d8": { pt: "/visto-d8", en: "/visa-d8" },
  "/sobre-nos": GLOBAL_ROUTES.about,
  "/about-us": GLOBAL_ROUTES.about,
  "/contato": GLOBAL_ROUTES.contact,
  "/contact": GLOBAL_ROUTES.contact,
  "/consultoria-legal": { pt: "/consultoria-legal", en: "/legal-consulting" },
  "/legal-consulting": { pt: "/consultoria-legal", en: "/legal-consulting" },
  "/financiamento": { pt: "/financiamento", en: "/financing" },
  "/financing": { pt: "/financiamento", en: "/financing" },
  "/comprar-casa": DESTINATION_ROUTES.portugal.buyProperty,
  "/politica-privacidade": GLOBAL_ROUTES.privacy,
  "/privacy-policy": GLOBAL_ROUTES.privacy,
  "/termos-uso": GLOBAL_ROUTES.terms,
  "/terms-of-use": GLOBAL_ROUTES.terms,
  "/rgpd": GLOBAL_ROUTES.gdpr,
  "/gdpr": GLOBAL_ROUTES.gdpr,
};

/**
 * Bilingual pages that keep one slug on both hosts.
 * Declared here, like visas/vistos, so the pair is in the map.
 * The slug is the same; pt-PT uses the .pt host and en-GB / x-default use the .com host.
 * Two articles use a different PT slug and stay out of this list:
 * /blog/golden-visa-2026-still-exists and /blog/d7-income-requirements-2026.
 */
export const SAME_SLUG_LANGUAGE_PATHS = [
  "/blog",
  "/pathways",
  "/interactive",
  "/parceiros",
  "/blog/licenca-residencia",
  "/blog/comprar-casa",
  "/blog/mercado-lisboa",
  "/blog/investir-imoveis",
  "/blog/tendencias-mercado",
  "/blog/custo-vida",
  "/blog/roteiro-mudanca",
  "/blog/saude-educacao",
  "/blog/nomada-digital",
  "/blog/aposentadoria",
  "/blog/nhr-fiscal",
  "/blog/retiring-to-portugal-from-ireland",
  "/blog/d7-vs-d8-visa",
  "/blog/startup-visa-portugal-2026",
  "/blog/healthcare-portugal-peace-of-mind",
  "/blog/reunificacao-familiar-portugal",
  "/blog/move-to-portugal-property-consultation",
  "/blog/visto-d8-nomada-digital-elite",
  "/blog/visto-d4-capital-humano",
  "/blog/golden-visa-requirements-portugal-2026",
  "/blog/golden-visa-vs-d7-portugal-2026",
  "/blog/portugal-d7-visa-eligibility-2026",
  "/blog/retirement-visa-portugal-timeline-2026",
  "/blog/portugal-retirement-visa-americans-2026",
  "/blog/spain-residency-pathways-2026",
  "/blog/italy-residency-pathways-2026",
  "/blog/greece-residency-pathways-2026",
  "/blog/d7-visa-refusal-reasons-2026",
  "/blog/escolher-destino-europa-pathways",
  "/blog/europe",
  "/blog/portugal",
  "/blog/spain",
  "/blog/italy",
  "/blog/greece",
  "/blog/house-buying-service-portugal",
] as const;

function sameSlugLanguageRoutes(): Record<string, LocalizedPath> {
  return Object.fromEntries(
    SAME_SLUG_LANGUAGE_PATHS.map((path) => [path, { pt: path, en: path }]),
  );
}

/** Full PT↔EN map: legacy slugs + destination routes + same-slug bilingual pages. */
export function buildSiteLanguageRouteMap(): Record<string, LocalizedPath> {
  return {
    ...LEGACY_SITE_LANGUAGE_ROUTES,
    ...buildAllDestinationsLanguageRouteMap(),
    ...healthcareSubpageLanguageMap(),
    ...sameSlugLanguageRoutes(),
  };
}

/**
 * Localized slugs for hreflang. A path outside the map keeps the same slug
 * on both languages; SEO and the sitemap then put that slug on each host.
 */
export function resolveLocalizedPaths(path: string): LocalizedPath {
  const map = buildSiteLanguageRouteMap();
  return map[path] ?? { pt: path, en: path };
}

/**
 * Canonical path for a page served on a long destination URL.
 * The live long URL wins. A legacy short URL falls back to the page language,
 * because that short URL 308s to the long one and must not be the canonical.
 */
export function canonicalPathForRoute(
  pathname: string,
  route: LocalizedPath,
  fallback: "pt" | "en",
): string {
  if (pathname === route.en || pathname.startsWith(`${route.en}/`)) return route.en;
  if (pathname === route.pt || pathname.startsWith(`${route.pt}/`)) return route.pt;
  return route[fallback];
}

export function getDestinationHubPaths(): string[] {
  return Object.values(DESTINATION_ROUTES).map((routes) => routes.hub.pt);
}
